<template>
  <div class="calendar-view">
    <!-- The header of the calendar -->
    <div class="calendar-control-row">
      <!-- Header for the previous month calendar -->
      <div class="calendar-header previous">
        <Icon size="18" @click="moveToPreviousMonth">arrow_back</Icon>
        <h4>{{ `${previousMonth.month} ${previousMonth.year}` }}</h4>
      </div>
      <!-- Header for the current month calendar -->
      <div
        :class="[
          'calendar-header',
          'current',
          { disabled: shouldDisableNextMonth },
        ]"
      >
        <h4>{{ `${currentMonth.month} ${currentMonth.year}` }}</h4>
        <Icon size="18" @click="moveToNextMonth">arrow_forward</Icon>
      </div>
    </div>
    <!-- Dual Month calendar -->
    <div class="multiple-calendars">
      <!-- Month calendar for previous month -->
      <div class="calendar previous">
        <div class="day-names">
          <span v-for="day in days" :key="day">{{ day }}</span>
        </div>
        <div class="dates" @mouseleave="setActiveHover(null)">
          <span
            v-for="(item, index) in previousMonth.days"
            :key="index"
            @click="dateClicked(item)"
            @mouseenter="setActiveHover(item)"
            :class="[
              { active: userDateStart ? item.time === userDateStart : false },
              { end: userDateEnd ? item.time === userDateEnd : false },
              {
                'in-range':
                  userDateStart && !userDateEnd && activeHoverItem
                    ? item.time <= activeHoverItem.time &&
                      item.time >= userDateStart
                    : false,
              },
              {
                'in-range':
                  userDateStart &&
                  userDateEnd &&
                  item.time > userDateStart &&
                  item.time < userDateEnd,
              },
              {
                'single-date':
                  userDateStart && userDateEnd && userDateStart === userDateEnd
                    ? true
                    : false,
              },
              { disabled: max && item.time > max ? true : false },
            ]"
            :style="{
              gridColumnStart:
                index === 0 ? `${previousMonth.days[0].day + 1}` : `auto`,
            }"
            >{{ item.date }}</span
          >
        </div>
      </div>
      <!-- Month calendar for current month -->
      <div class="calendar current">
        <div class="day-names">
          <span v-for="day in days" :key="day">{{ day }}</span>
        </div>
        <div class="dates">
          <span
            v-for="(item, index) in currentMonth.days"
            :key="index"
            @click="dateClicked(item)"
            @mouseenter="setActiveHover(item)"
            :class="[
              { active: userDateStart ? item.time === userDateStart : false },
              { end: userDateEnd ? item.time === userDateEnd : false },
              {
                'in-range':
                  userDateStart && !userDateEnd && activeHoverItem
                    ? item.time <= activeHoverItem.time &&
                      item.time >= userDateStart
                    : false,
              },
              {
                'in-range':
                  userDateStart &&
                  userDateEnd &&
                  item.time > userDateStart &&
                  item.time < userDateEnd,
              },
              {
                'single-date':
                  userDateStart && userDateEnd
                    ? item.time === userDateStart && item.time === userDateEnd
                    : false,
              },
              { disabled: max && item.time > max ? true : false },
            ]"
            :style="{
              gridColumnStart:
                index === 0 ? `${currentMonth.days[0].day + 1}` : `auto`,
            }"
            >{{ item.date }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const dayNames = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default {
  props: {
    value: Array,
    max: Number,
  },
  data() {
    return {
      days: dayNames,
      months: monthNames,
      currentDate: null,
      activeHoverItem: null,
      userDateStart: this.getMinimalDate(this.value[0]),
      userDateEnd: this.getMinimalDate(this.value[1]),
    };
  },
  computed: {
    currentMonth() {
      const date = new Date(this.currentDate);
      return this.getDateData(date);
    },
    previousMonth() {
      const date = new Date(this.currentDate);
      return this.getDateData(new Date(date.setMonth(date.getMonth() - 1)));
    },
    /**
     * Check if the next month button should be disabled,
     * based on [if current month is greater than on equal to max month]
     */
    shouldDisableNextMonth() {
      if (this.max) {
        let maxDate = new Date(this.max);
        let maxCode = parseFloat(
          `${maxDate.getFullYear()}${maxDate.getMonth()}`
        );
        let curDate = new Date(this.currentDate);
        let curCode = parseFloat(
          `${curDate.getFullYear()}${curDate.getMonth()}`
        );
        if (curCode >= maxCode) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
  },
  methods: {
    // ripps of any time aspect in the epoch. Converts to pure date.
    getMinimalDate(epochs) {
      if (!epochs) return null;
      let date = new Date(epochs);
      date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return date.getTime();
    },
    moveToPreviousMonth() {
      let currentDate = new Date(this.currentDate);
      let currentMonth = currentDate.getMonth();
      if (currentMonth === 1) {
        currentDate = new Date(currentDate.setMonth(12));
        currentDate = new Date(
          currentDate.setFullYear(currentDate.getFullYear() - 1)
        );
      } else {
        currentDate = new Date(
          currentDate.setMonth(currentDate.getMonth() - 1)
        );
      }
      this.currentDate = currentDate.getTime();
    },
    moveToNextMonth() {
      let currentDate = new Date(this.currentDate);
      let currentMonth = currentDate.getMonth();
      // check if the next month is more than max
      if (this.max) {
        let maxDate = new Date(this.max);
        if (!(maxDate.getMonth() > currentMonth)) return;
      }

      if (currentMonth === 12) {
        currentDate = new Date(currentDate.setMonth(1));
        currentDate = new Date(
          currentDate.setFullYear(currentDate.getFullYear() + 1)
        );
      } else {
        currentDate = new Date(
          currentDate.setMonth(currentDate.getMonth() + 1)
        );
      }
      this.currentDate = currentDate.getTime();
    },
    dateClicked(item) {
      // don't select if the time is more than max
      if (this.max && item.time > this.max) return;

      this.$emit("click");

      if (this.userDateStart && !this.userDateEnd) {
        if (this.userDateStart > item.time) {
          this.userDateStart = item.time;
          this.userDateEnd = null;
        } else {
          this.userDateEnd = item.time;
          this.$emit("input", [this.userDateStart, this.userDateEnd]);
        }
        return;
      }
      this.userDateStart = item.time;
      this.userDateEnd = null;
    },
    setActiveHover(item) {
      if (!item) return;
      // don't select if the time is more than max
      if (this.max && item.time > this.max) return;

      this.activeHoverItem = item;
    },
    getDateData(date) {
      return {
        month: this.months[date.getMonth()],
        year: date.getFullYear(),
        days: this.getDaysInMonth(date.getFullYear(), date.getMonth()),
      };
    },
    getDaysInMonth(year, month) {
      let monthIndex = null;
      if (month === 11) {
        monthIndex = 0;
      } else {
        monthIndex = month + 1;
      }
      let date = new Date(year, month, 1);
      let result = [];
      while (date.getMonth() !== monthIndex) {
        result.push({
          date: date.getDate(),
          day: date.getDay(),
          time: date.getTime(),
        });
        date.setDate(date.getDate() + 1);
      }

      return result;
    },
  },
  mounted() {
    this.currentDate =
      this.value && this.value[1] ? new Date(this.value[1]) : new Date();
  },
};
</script>

<style lang="scss" scoped>
.calendar-view {
  display: flex;
  flex-direction: column;
  margin-top: 15px;
  padding: 10px 0;

  .calendar-control-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 15px;

    .calendar-header {
      flex: 1;
      display: flex;
      flex-direction: row;
      align-items: center;

      &.disabled {
        i {
          opacity: 0.3;
        }
      }

      h4 {
        flex: 1;
        font-weight: bold;
        text-align: center;
      }

      i {
        padding: 10px;
        background: var(--border-color);
        border-radius: 50%;
        transition: 0.3s all ease-in-out;

        &:hover {
          background: var(--color-primary-light);
          color: var(--color-primary);
          cursor: pointer;
        }

        &:active {
          transform: scale(0.9);
        }
      }
    }
  }

  .multiple-calendars {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 15px;

    .day-names {
      display: grid;
      grid-template-columns: repeat(7, 51px);
      padding: 10px 5px;

      span {
        text-align: center;
        opacity: 0.6;
      }
    }

    .dates {
      display: grid;
      padding: 5px 10px;
      grid-template-columns: repeat(7, 51px);

      span {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 51px;
        height: 51px;
        border-radius: 5px;

        &:hover {
          background: var(--border-color);
          cursor: pointer;
        }

        &.in-range {
          background: var(--color-primary-light);
          border-radius: 0;
        }

        &.active {
          background: var(--color-primary) !important;
          color: var(--color-secondary);
          border-radius: 5px 0 0 5px;
        }

        &.end {
          background: var(--color-primary) !important;
          color: var(--color-secondary);
          border-radius: 0px 5px 5px 0;
        }

        &.single-date {
          border-radius: 5px !important;
        }

        &.disabled {
          opacity: 0.2;
        }
      }
    }
  }
}
</style>
